import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  HelpCircle, 
  Mail, 
  Phone, 
  MapPin, 
  Send, 
  ChevronDown,
  ChevronUp,
  MessageSquare
} from 'lucide-react';
import { Card, Button, Input } from '../components/ui';
import { useToast } from '../hooks/use-toast';
import { BRAND } from '../config/brand';
import './HelpPage.css';

export function HelpPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [openFaqId, setOpenFaqId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const toggleFaq = (id) => {
    setOpenFaqId(openFaqId === id ? null : id);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // Simulate API call
    setTimeout(() => {
      toast({
        title: t('help.toast.submittedTitle'),
        description: t('help.toast.submittedDescription'),
        variant: 'success'
      });
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      setSubmitting(false);
    }, 1000);
  };

  const faqData = [
    { id: 1, question: t('help.faq.1.question'), answer: t('help.faq.1.answer') },
    { id: 2, question: t('help.faq.2.question'), answer: t('help.faq.2.answer') },
    { id: 3, question: t('help.faq.3.question'), answer: t('help.faq.3.answer') },
    { id: 4, question: t('help.faq.4.question'), answer: t('help.faq.4.answer') },
    { id: 5, question: t('help.faq.5.question'), answer: t('help.faq.5.answer') },
    { id: 6, question: t('help.faq.6.question'), answer: t('help.faq.6.answer') }
  ];

  return (
    <div className="help-page">
      <div className="help-page__container">
        {/* Header */}
        <div className="help-page__header">
          <div className="help-page__header-icon">
            <HelpCircle size={32} />
          </div>
          <h1 className="help-page__title">{t('help.page.title')}</h1>
          <p className="help-page__subtitle">
            {t('help.page.subtitle')}
          </p>
        </div>

        {/* Contact Information Cards */}
        <div className="help-page__contact-cards">
          <Card className="help-page__contact-card">
            <div className="help-page__contact-icon" style={{ backgroundColor: '#0066FF14' }}>
              <Mail size={24} style={{ color: '#0066FF' }} />
            </div>
            <div className="help-page__contact-info">
              <h3 className="help-page__contact-label">{t('help.page.contact.emailLabel')}</h3>
              <a href={`mailto:${BRAND.supportEmail}`} className="help-page__contact-value">
                {BRAND.supportEmail}
              </a>
            </div>
          </Card>

          <Card className="help-page__contact-card">
            <div className="help-page__contact-icon" style={{ backgroundColor: '#00C48C14' }}>
              <Phone size={24} style={{ color: '#00C48C' }} />
            </div>
            <div className="help-page__contact-info">
              <h3 className="help-page__contact-label">{t('help.page.contact.phoneLabel')}</h3>
              <a href="tel:+390123456789" className="help-page__contact-value">
                +39 012 345 6789
              </a>
            </div>
          </Card>

          <Card className="help-page__contact-card">
            <div className="help-page__contact-icon" style={{ backgroundColor: '#FF950014' }}>
              <MapPin size={24} style={{ color: '#FF9500' }} />
            </div>
            <div className="help-page__contact-info">
              <h3 className="help-page__contact-label">{t('help.page.contact.addressLabel')}</h3>
              <p className="help-page__contact-value">
                Via Agricola 123<br />
                00100 Rome, Italy
              </p>
            </div>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="help-page__content">
          {/* FAQ Section */}
          <div className="help-page__section">
            <h2 className="help-page__section-title">{t('help.page.sections.faq')}</h2>
            <div className="help-page__faq-list">
              {faqData.map((faq) => (
                <Card key={faq.id} className="help-page__faq-item">
                  <button
                    className="help-page__faq-question"
                    onClick={() => toggleFaq(faq.id)}
                  >
                    <span>{faq.question}</span>
                    {openFaqId === faq.id ? (
                      <ChevronUp size={20} />
                    ) : (
                      <ChevronDown size={20} />
                    )}
                  </button>
                  {openFaqId === faq.id && (
                    <div className="help-page__faq-answer">
                      {faq.answer}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </div>

          {/* Contact Form Section */}
          <div className="help-page__section">
            <h2 className="help-page__section-title">{t('help.page.sections.submitRequest')}</h2>
            <Card className="help-page__form-card">
              <form onSubmit={handleSubmit} className="help-page__form">
                <div className="help-page__form-group">
                  <label htmlFor="name" className="help-page__form-label">
                    {t('help.form.nameLabel')}
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder={t('help.form.namePlaceholder')}
                    required
                  />
                </div>

                <div className="help-page__form-group">
                  <label htmlFor="email" className="help-page__form-label">
                    {t('help.form.emailLabel')}
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder={t('help.form.emailPlaceholder')}
                    required
                  />
                </div>

                <div className="help-page__form-group">
                  <label htmlFor="subject" className="help-page__form-label">
                    {t('help.form.subjectLabel')}
                  </label>
                  <Input
                    id="subject"
                    name="subject"
                    type="text"
                    value={formData.subject}
                    onChange={handleInputChange}
                    placeholder={t('help.form.subjectPlaceholder')}
                    required
                  />
                </div>

                <div className="help-page__form-group">
                  <label htmlFor="message" className="help-page__form-label">
                    {t('help.form.messageLabel')}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder={t('help.form.messagePlaceholder')}
                    className="help-page__form-textarea"
                    rows={6}
                    required
                  />
                </div>

                <Button
                  type="submit"
                  className="help-page__form-submit"
                  disabled={submitting}
                >
                  <Send size={18} />
                  {submitting ? t('help.form.submitting') : t('help.form.submit')}
                </Button>
              </form>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
